import { ChangeDetectorRef, Directive, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { LocalStorage } from '@df/browser/storage/decorator/local-storage';
import { NCatalogUi } from '@df/catalog-ui/df-catalog-ui.config';
import { IFilterDefinition, IListingSetup } from '@df/catalog-ui/listing/listing';
import { IProductListingConfig, ProductListing } from '@df/catalog-ui/listing/product-listing';
import { ProductListingFactory } from '@df/catalog-ui/listing/product-listing.factory';
import { IListingSizeOption } from '@df/catalog-ui/listing/size/listing-size';
import { IListingSortOption } from '@df/catalog-ui/listing/sort/listing-sort';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Search } from '@df/catalog/search/search';
import { App } from '@df/core/app';
import { SearchResult } from '@df/search/search-result';
import { Subscription } from 'rxjs';
import { NSearch } from './df-search.config';

@Directive()
// eslint-disable-next-line @angular-eslint/directive-class-suffix
export abstract class SearchViewComponentDef<T extends Product<IProductDataV2> = Product<IProductDataV2>> implements OnInit, OnDestroy {
    protected subscriptions: Subscription = new Subscription();
    /**
     * Listing model
     */
    listing: ProductListing<T> | undefined;

    /**
     * Listing model subscription
     */
    listingSubscription?: Subscription;

    /**
     * Search results
     */
    _result?: SearchResult;

    /**
     * Last category path so we can get back on back to stor button
     */
    @LocalStorage('lastCategoryPath')
    protected lastCategoryPath?: string;

    /**
     * Router service
     */
    protected router = inject(Router);

    readonly app = inject(App);

    protected productListingFactory = inject(ProductListingFactory);

    private moduleConfig = inject(NSearch.OPTIONS);
    protected uiModuleConfig = inject(NCatalogUi.OPTIONS);

    /**
     * Class constructor
     */
    constructor(
        protected _ref: ChangeDetectorRef,
        protected activatedRoute: ActivatedRoute
    ) {
        // this.result = activatedRoute.snapshot.data.searchResult;
    }

    /**
     * 1. sync category path
     * 2. create listing
     */
    ngOnInit(): void {
        this.subscriptions.add(
            this.activatedRoute.data.subscribe(data => {
                this.applyRouteData(data);
            })
        );
    }

    protected applyRouteData(routeData: Data) {
        this.result = routeData['searchResult'] as SearchResult;
        this.lastCategoryPath = routeData['path'];
        this.onResultChange();
    }

    ngOnDestroy(): void {
        this.unsubscribe();
        this.subscriptions.unsubscribe();
    }

    /**
     * Creates new listing on model change
     */
    set result(result: SearchResult) {
        this._result = result;
    }

    /**
     * Search results getter
     */
    get result(): SearchResult | undefined {
        return this._result;
    }

    /**
     * Filters provider for listing
     */
    protected getFilterDefs(): (IFilterDefinition | string)[] {
        return (
            this.moduleConfig.listing.filters ||
            this.app.resources.attribute
                .getAllExisting()
                .filter(i => i.data!.filterable)
                .map(i => i.id)
        );
    }

    /**
     * Sort options provider for listing
     */
    protected getSortOps(): IListingSortOption[] | undefined {
        return this.moduleConfig.listing.sort || this.uiModuleConfig.listing.category.sort;
    }

    /**
     * Product ids privider for listing
     */
    protected getProductIds(): number[] {
        return this.result?.products?.map(i => i.id) || [];
    }

    protected getPagesIds(): number[] {
        return this.result?.data.result?.page || [];
    }

    protected unsubscribe(): void {
        if (this.listingSubscription) {
            this.listingSubscription.unsubscribe();
            this.listingSubscription = undefined;
        }
    }

    /**
     * Size options provider for listing
     */
    protected getSizeOps(): IListingSizeOption[] | undefined {
        return this.moduleConfig.listing.size || this.uiModuleConfig.listing.category.size;
    }

    protected onResultChange(): void {
        this.unsubscribe();
        this.listing = this.productListingFactory.create<T>(this.getListingConfig(), this.getProductIds(), this.getListingSetup());
        if (this.result) {
            const searchModel = new Search(this.result.term);
            searchModel.setData({
                id: this.result.term,
                url: this.router.url,
                verbosity: 1,
                type: 'search',
                breadcrumbs: [{ label: this.getSearchCrumbLabel(this.result.term), url: this.router.url }]
            });
            this.app.meta.model = searchModel;
        } else {
            this.app.meta.model = undefined;
        }
        this.listingSubscription = this.listing.subscribe(() => this._ref.detectChanges());
    }

    protected getSearchCrumbLabel(term: string) {
        return 'Search results: ' + term;
    }

    protected getListingConfig(): IProductListingConfig {
        return {
            type: 'product',
            source: this.result,
            infScrollStep: this.moduleConfig.listing.infScrollStep || this.uiModuleConfig.listing.category.infScrollStep,
            stockCheck: this.moduleConfig.options.view.stockCheck,
            storage: true,
            custom: {
                pioAllowed: false
            }
        };
    }

    protected getListingSetup(): IListingSetup<T> {
        return {
            filtersDefinitions: this.getFilterDefs(),
            sortOptions: this.getSortOps(),
            sizeOptions: this.getSizeOps(),
            defaultSize: this.moduleConfig.listing.defaultSize || this.uiModuleConfig.listing.category.defaultSize
        };
    }
}
